import { ItemCountBadge } from "@/components/ItemCountBadge";
import { TodoInput } from "@/components/TodoInput";
import { TodoList } from "@/components/TodoList";
import { useDrawer } from "@/contexts/drawer-context";
import { useDeleteCompletedTodos } from "@/hooks/use-delete-completed-todos";
import { useLabels } from "@/hooks/use-labels";
import { useReorderTodo } from "@/hooks/use-reorder-todo";
import {
  useCreateTodo,
  useDeleteTodo,
  useTodos,
  useToggleTodo,
} from "@/hooks/use-todos";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Menu } from "lucide-react";

export const Route = createFileRoute("/_app/labels/$labelId")({
  component: LabelTodosPage,
});

function LabelTodosPage() {
  const { labelId } = Route.useParams();
  const navigate = useNavigate();
  const { open: openDrawer } = useDrawer();
  const { data: labels } = useLabels();
  const { data: todos, isLoading } = useTodos(labelId);
  const createTodo = useCreateTodo();
  const toggleTodo = useToggleTodo();
  const deleteTodo = useDeleteTodo();
  const reorderTodo = useReorderTodo();
  const deleteCompleted = useDeleteCompletedTodos();

  const isUnlabeled = labelId === "none";
  const label = labels?.find((l) => l.id === labelId);
  const labelName = isUnlabeled ? "未分類" : (label?.name ?? "不明なラベル");

  const handleAdd = (title: string) => {
    const labelIds = isUnlabeled ? undefined : [labelId];
    createTodo.mutate({ title, labelIds });
  };

  const handleToggle = (id: string, checked: boolean) => {
    toggleTodo.mutate({ id, checked });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ヘッダー */}
      <header className="relative flex items-center justify-between border-b px-4 py-3">
        <button
          type="button"
          onClick={openDrawer}
          className="z-10 rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="メニュー"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="pointer-events-none absolute inset-x-0 text-center text-xl font-bold text-foreground">
          {labelName}
        </h1>
        <ItemCountBadge
          todos={todos ?? []}
          onDeleteCompleted={() => deleteCompleted.mutate(labelId)}
        />
      </header>

      {/* TODO リスト */}
      <div className="flex-1 overflow-y-auto px-4">
        {isLoading ? (
          <p className="py-8 text-center text-muted-foreground">
            読み込み中...
          </p>
        ) : (
          <TodoList
            todos={todos ?? []}
            onToggle={handleToggle}
            onDelete={(id) => deleteTodo.mutate(id)}
            onReorder={(id, sortOrder) => reorderTodo.mutate({ id, sortOrder })}
          />
        )}
      </div>

      {/* TODO 入力（画面下部固定） */}
      <div className="sticky bottom-0 border-t bg-background px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3">
        <TodoInput
          onAdd={handleAdd}
          isPending={createTodo.isPending}
          onHome={() => navigate({ to: "/labels" })}
        />
      </div>
    </div>
  );
}
